import React, { useCallback, useEffect, useState } from "react";
import {
  activateTrade,
  addTrade,
  deactivateTrade,
  listTrades,
  updateTradeName,
} from "../../../helpers/catalogs/tradesCatalog";
import { usePermissions } from "../../../hooks/usePermissions";
import { AdminFormCard } from "../shared/AdminFormCard";
import { AdminLegacyHeader } from "../shared/AdminFormCard";
import { CPInput } from "../../CPInput";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskTable } from "../../desktop/DeskTable";
import "../adminDesktop.css";

export function AdminTradesPanel({ onBack, showLegacyHeader = false }) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const [trades, setTrades] = useState([]);
  const [newTradeName, setNewTradeName] = useState("");
  const [editingTradeId, setEditingTradeId] = useState(null);
  const [editingTradeName, setEditingTradeName] = useState("");
  const [loading, setLoading] = useState(true);

  const loadTrades = useCallback(async () => {
    if (!actor) {
      return;
    }

    try {
      setLoading(true);
      const data = await listTrades(actor, { activeOnly: false });
      setTrades(data);
    } catch (err) {
      console.error("LOAD TRADES ERROR:", err);
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const handleAddTrade = async () => {
    try {
      await addTrade(newTradeName, undefined, actor);
      setNewTradeName("");
      loadTrades();
    } catch (err) {
      console.error("ADD TRADE ERROR:", err);
      alert(err.message || "Failed to add trade.");
    }
  };

  const handleSaveTradeName = async (trade) => {
    try {
      await updateTradeName(trade.trade_id, editingTradeName, actor);
      setEditingTradeId(null);
      setEditingTradeName("");
      loadTrades();
    } catch (err) {
      console.error("UPDATE TRADE ERROR:", err);
      alert(err.message || "Failed to update trade.");
    }
  };

  const handleToggleActive = async (trade) => {
    try {
      if (trade.is_active) {
        await deactivateTrade(trade.trade_id, actor);
      } else {
        await activateTrade(trade.trade_id, actor);
      }
      loadTrades();
    } catch (err) {
      console.error("TOGGLE TRADE ERROR:", err);
      alert(err.message || "Failed to update trade.");
    }
  };

  const desktopContent = (
    <>
      <p className="adm-note">
        Organization trades used for payroll rate resolution. Assign trades on
        employee profiles.
      </p>
      <div className="adm-main-side">
        <div className="adm-stack">
          {loading ? (
            <p className="adm-note">Loading trades...</p>
          ) : trades.length === 0 ? (
            <DeskEmpty title="No trades">Add your first trade to start assigning payroll rates.</DeskEmpty>
          ) : (
            <DeskTable
              caption="Organization trades"
              rows={trades}
              rowKey={(trade) => trade.trade_id}
              rowClassName={(trade) => (trade.is_active ? "" : "is-muted")}
              columns={[
                {
                  key: "name",
                  label: "Trade",
                  render: (trade) =>
                    editingTradeId === trade.trade_id ? (
                      <div className="adm-edit">
                        <CPInput
                          className="cp-input-group--compact"
                          value={editingTradeName}
                          onChange={setEditingTradeName}
                          aria-label={`Rename ${trade.trade_name}`}
                        />
                      </div>
                    ) : (
                      <span className="dt-strong">{trade.trade_name}</span>
                    ),
                },
                {
                  key: "status",
                  label: "Status",
                  width: "120px",
                  render: (trade) => (
                    <DeskPill tone={trade.is_active ? "green" : "muted"}>
                      {trade.is_active ? "Active" : "Inactive"}
                    </DeskPill>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  align: "right",
                  width: "230px",
                  render: (trade) =>
                    editingTradeId === trade.trade_id ? (
                      <span className="adm-rowact">
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm adm-primary"
                          onClick={() => handleSaveTradeName(trade)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => {
                            setEditingTradeId(null);
                            setEditingTradeName("");
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="adm-rowact">
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => {
                            setEditingTradeId(trade.trade_id);
                            setEditingTradeName(trade.trade_name);
                          }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className={`kpi-back-btn adm-sm${trade.is_active ? " adm-soft" : ""}`}
                          onClick={() => handleToggleActive(trade)}
                        >
                          {trade.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </span>
                    ),
                },
              ]}
            />
          )}
        </div>

        <AdminFormCard title="ADD TRADE">
          <CPInput
            label="New trade name"
            value={newTradeName}
            onChange={setNewTradeName}
            placeholder="New trade name"
          />
          <div className="adm-actions adm-actions--plain">
            <button
              type="button"
              className="kpi-back-btn adm-primary"
              onClick={handleAddTrade}
            >
              Add Trade
            </button>
          </div>
        </AdminFormCard>
      </div>
    </>
  );

  const content = isDesktop && !showLegacyHeader ? desktopContent : (
    <AdminFormCard title="ORGANIZATION CONFIGURATION — TRADES">
      <p className="profile-value" style={{ marginBottom: "12px" }}>
        Organization trades used for payroll rate resolution. Assign trades on
        employee profiles.
      </p>

      {loading ? (
        <p className="profile-value">Loading trades...</p>
      ) : trades.length === 0 ? (
        <p className="profile-value">No trades.</p>
      ) : (
        trades.map((trade) => (
          <div
            key={trade.trade_id}
            className="profile-value"
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            {editingTradeId === trade.trade_id ? (
              <>
                <div style={{ flex: "1 1 180px" }}>
                  <CPInput
                    className="cp-input-group--compact"
                    value={editingTradeName}
                    onChange={setEditingTradeName}
                    aria-label={`Rename ${trade.trade_name}`}
                  />
                </div>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleSaveTradeName(trade)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => {
                    setEditingTradeId(null);
                    setEditingTradeName("");
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span style={{ flex: "1 1 180px" }}>
                  {trade.trade_name}
                  {!trade.is_active ? " — inactive" : ""}
                </span>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => {
                    setEditingTradeId(trade.trade_id);
                    setEditingTradeName(trade.trade_name);
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleToggleActive(trade)}
                >
                  {trade.is_active ? "Deactivate" : "Activate"}
                </button>
              </>
            )}
          </div>
        ))
      )}

      <CPInput
        label="New trade name"
        value={newTradeName}
        onChange={setNewTradeName}
        placeholder="New trade name"
      />

      <button
        type="button"
        className="kpi-back-btn"
        style={{ marginTop: "10px" }}
        onClick={handleAddTrade}
      >
        Add Trade
      </button>
    </AdminFormCard>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="TRADE MANAGEMENT" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}
